/**
 * Duct Placeholder Tool - BIM 2025 Grade
 * 
 * Creates placeholder ducts for schematic design.
 * Features:
 * - Single-line representation in 2D
 * - Simple 3D volume
 * - "Convert to Duct" functionality
 * - System inheritance
 * - Connection to air terminals/equipment
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface DuctPlaceholderGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    width: number;
    height: number;
    diameter?: number; // For round
    shape: 'Rectangular' | 'Round' | 'Oval';
}

export interface DuctPlaceholderElement extends Element {
    category: 'Duct Placeholders';
    geometryData: DuctPlaceholderGeometryData;
    systemType: string;
}

export class DuctPlaceholderTool extends BaseTool {
    id = 'duct_placeholder';
    name = 'Duct Placeholder';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    // Options
    private width: number = 300;
    private height: number = 300;
    private shape: 'Rectangular' | 'Round' = 'Rectangular';
    private systemType: string = 'Supply Air';
    private elevation: number = 2700;

    constructor() {
        super();
        this.updateOptionsDisplay();
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const pt = this.getIntersection(event);
        if (pt) {
            const snapped = this.snapToGrid(pt);
            snapped.z = this.elevation / 1000;

            this.points.push(snapped);

            if (this.points.length > 1) {
                // Must have at least start and end
                this.createPlaceholderSegment(
                    this.points[this.points.length - 2],
                    this.points[this.points.length - 1]
                );

                // Keep the last point as the start of the next segment (chaining)
                // Behavior: [p1, p2, p3...]
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const pt = this.getIntersection(event);
        if (pt) {
            const snapped = this.snapToGrid(pt);
            snapped.z = this.elevation / 1000;
            this.currentPoint = snapped;
            this.updatePreview();
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.reset();
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const last = this.points[this.points.length - 1];
            this.context.appStore.setToolPreview({
                type: 'line_3d',
                start: last,
                end: this.currentPoint,
                color: '#0000FF', // Blue for supply
                baseOffset: this.elevation / 1000
            });
        }
    }

    private reset() {
        this.points = [];
        this.currentPoint = null;
        this.context?.appStore.setToolPreview(null);
    }

    private createPlaceholderSegment(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        const geometryData: DuctPlaceholderGeometryData = {
            start: start.toArray() as [number, number, number],
            end: end.toArray() as [number, number, number],
            width: this.width,
            height: this.height,
            shape: this.shape
        };

        const placeholder: DuctPlaceholderElement = {
            id: uuidv4(),
            category: 'Duct Placeholders',
            type: `Placeholder: ${this.width}x${this.height}`,
            parameters: {
                'Width': this.width,
                'Height': this.height,
                'System Type': this.systemType,
                'Elevation': this.elevation
            },
            constraints: [],
            geometryData,
            systemType: this.systemType
        };

        this.context.bimStore.addElement(placeholder);
        // Note: Fittings are NOT created for placeholders, just visual cues if needed. 
    }

    private updateOptionsDisplay() {
        // Mock UI update
    }

    // Stub
    onPointerUp() { }
}
